/**
 * Which URL to draw an image from. catalog-service now records a 160px `thumbnailUrl` beside the
 * optimized WebP (api-changes §48); older rows and older deploys have none, so every pick falls
 * back thumbnail → optimized → source.
 */

/** The image fields a pick can use; every one is optional because older payloads lack them. */
export interface ImageUrlFields {
  thumbnailUrl?: string | null | undefined;
  optimizedUrl?: string | null | undefined;
  sourceUrl?: string | null | undefined;
}

/** The first candidate that is a non-empty string (an empty URL is "none", not a URL). */
function firstUrl(...candidates: (string | null | undefined)[]): string | null {
  return candidates.find((url): url is string => typeof url === 'string' && url !== '') ?? null;
}

/** For a small thumb (≤ ~160px): the thumbnail, else the optimized copy, else the source. */
export function pickThumbUrl({
  thumbnailUrl,
  optimizedUrl,
  sourceUrl,
}: ImageUrlFields): string | null {
  return firstUrl(thumbnailUrl, optimizedUrl, sourceUrl);
}

/** For a large view (preview, lightbox): the optimized copy, else the source — never the 160px thumb. */
export function pickFullUrl({ optimizedUrl, sourceUrl }: ImageUrlFields): string | null {
  return firstUrl(optimizedUrl, sourceUrl);
}

/** A source → optimized pair as the image reads return it (`OptimizedImageRead` keeps `thumbnailUrl` on `optimized`). */
export interface ImagePairLike {
  sourceUrl: string;
  optimized?: { url: string; thumbnailUrl?: string | null | undefined } | null | undefined;
}

/** Thumb URL of a source → optimized pair (falls back to the source URL, which is always set). */
export function pairThumbUrl(pair: ImagePairLike): string {
  return firstUrl(pair.optimized?.thumbnailUrl, pair.optimized?.url) ?? pair.sourceUrl;
}
